import { ScalarType } from '@protobuf-ts/runtime'

import { enumMappers } from './enum'
import { messageMappers } from './message'
import { createMessageFieldInfo, isArrayFieldInfo, setDefaults } from './utils'

import type { FormSource } from '../types/shared/formSource'
import type { TransformSchema } from '../types/transformers'
import type { AnyObject } from '@policyfly/types/common'
import type { FieldInfo, IMessageType } from '@protobuf-ts/runtime'

/**
 * Converts a loaded program configuration object into the equivalent protobuf schema object.
 *
 * @example
 * import { SchemaSettings } from '@policyfly/protobuf'
 *
 * const settingsFile = await import('settings.ts')
 * const protobufSchema = convertSchema(SchemaSettings, settingsFile.default)
 */
export function convertSchema<
  T extends object,
  U extends TransformSchema<object, FormSource>
> (message: IMessageType<T>, schema: U): T {
  const definition = writeOrMap(message, schema)!
  return message.create(definition)
}

/**
 * @internal
 *
 * Default handler for repeated messages.
 * Can be overridden by specifying `mapToRepeated` for a message.
 */
export function writeOrMapRepeated<T extends object> (message: IMessageType<T>, configValue: object[]): T[] {
  return configValue.map((value) => {
    return writeOrMap(message, value) as T
  })
}

/**
 * @internal
 *
 * Utility to either use an existing mapper for a message or run it through the standard `writeMessage` function.
 * Also handles the setting of default values missing in config values.
 */
export function writeOrMap<T extends object> (message: IMessageType<T>, configValue: object): T | undefined {
  const { mapValue, defaults } = messageMappers.getMapper(message) ?? {}
  if (defaults) {
    configValue = setDefaults(configValue, defaults as () => object)
  }
  if (mapValue) {
    return mapValue(configValue) as T | undefined
  }
  return writeMessage(
    createMessageFieldInfo(message),
    configValue,
    message.create(),
  )
}

/**
 * @internal
 *
 * Using the `fieldInfo` for a message, converts the `configObject` and writes it to the `schemaObject`.
 *
 * Often it is better to use `writeOrMap` instead of this function.
 * However this can be useful to write a message within a mapper that only changes structure.
 * See `DisplayIf_Condition_Common` mapper as an example.
 *
 * @param fieldInfo - The fieldInfo for the message. Must be a message kind.
 * @param configObject - The program config object to convert.
 * @param schemaObject - The resulting protobuf schema object to write to.
 */
export function writeMessage<T extends object> (fieldInfo: FieldInfo, configObject: AnyObject, schemaObject: T): T {
  // shouldn't be passing anything except messages here
  if (fieldInfo.kind !== 'message') {
    console.warn('writeMessage called with non-message fieldInfo', fieldInfo)
    return schemaObject
  }
  // loop through all the fields of the message
  for (const field of fieldInfo.T().fields) {
    const fieldName = field.jsonName as keyof T
    if (!(fieldName in configObject)) continue
    const configValue = configObject[field.jsonName]
    switch (field.kind) {
      case 'scalar':
        schemaObject[fieldName] = configValue
        break
      case 'enum': {
        const { mapValue } = enumMappers.getMapper(field.T()[1]) ?? ({ mapValue: (v) => v })
        if (isArrayFieldInfo(field)) {
          if (Array.isArray(configValue)) {
            schemaObject[fieldName] = configValue.map(mapValue) as T[keyof T]
          }
        } else {
          schemaObject[fieldName] = mapValue(configValue) as T[keyof T]
        }
        break
      }
      case 'message': {
        const message = field.T()
        if (isArrayFieldInfo(field)) {
          const { mapToRepeated = writeOrMapRepeated.bind(null, message) } = messageMappers.getMapper(message) ?? {}
          schemaObject[fieldName] = mapToRepeated(configValue) as T[keyof T]
        } else {
          schemaObject[fieldName] = writeOrMap(message, configValue)
        }
        break
      }
      case 'map': {
        if (field.K !== ScalarType.STRING) {
          console.warn('Non-string map keys are not supported in convertSchema', field)
          continue
        }
        if (field.V.kind !== 'message') {
          console.warn('Non-message map values are not supported in convertSchema', field)
          continue
        }
        const message = field.V.T()
        const resultMap: Record<string, unknown> = {}
        for (const [key, value] of Object.entries(configValue)) {
          resultMap[key] = writeOrMap(message, value as object)
        }
        schemaObject[fieldName] = resultMap as T[keyof T]
        break
      }
    }
  }
  return schemaObject
}
